import React from 'react';
import api from '../lib/api';
import { useCrud } from '../lib/useCrud';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, MoreVertical, Building2, Star, IndianRupee, Edit, Trash2, Wallet, Copy, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';

const emptyForm = { name: '', bank: '', account_number: '', ifsc: '', branch: '', type: 'current', is_primary: false, current_balance: '' };

const TYPE_LABEL = { current: 'Current', savings: 'Savings', salary: 'Salary' };

/** Build a clipboard-friendly multi-line block of bank details, ready
 *  to paste into WhatsApp / Email. Skips empty fields automatically.
 *
 *  `companyName` is the user-facing legal entity (from Settings →
 *  Company Name) — that's what an external recipient expects to see
 *  for "Account Name", NOT the internal display label like
 *  "IDFC First Bank - 8432". Falls back to the display name when the
 *  company name isn't configured yet. */
const buildBankDetailsText = (a, companyName) => {
  const accountName = (companyName && companyName.trim()) || a.name;
  const rows = [
    ['Bank Name',      a.bank],
    ['Account Name',   accountName],
    ['Account Number', a.account_number],
    ['Account Type',   TYPE_LABEL[a.type] || a.type],
    ['Branch',         a.branch],
    ['IFSC',           a.ifsc],
  ];
  return rows.filter(([, v]) => (v != null && String(v).trim() !== ''))
    .map(([k, v]) => `${k}: ${v}`).join('\n');
};

export const BankAccountsPage = () => {
  const { data: accounts, loading, fetchData, create, update, remove } = useCrud('/bank-accounts');
  const confirm = useConfirmation();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('bank_accounts.create');
  const canEdit = hasPermission('bank_accounts.edit');
  const canDelete = hasPermission('bank_accounts.delete');
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [formData, setFormData] = React.useState(emptyForm);
  const [isEditing, setIsEditing] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState(null);
  const [balanceDialog, setBalanceDialog] = React.useState({ open: false, account: null });
  const [newBalance, setNewBalance] = React.useState('');
  // Company name from Settings (Settings → Company → Company Name). Used
  // as the "Account Name" line in the copied/shared bank details so
  // recipients see the legal entity, not our internal display label.
  const [companyName, setCompanyName] = React.useState('');
  React.useEffect(() => {
    let cancelled = false;
    api.get('/invoices/company-settings').then(r => {
      if (cancelled) return;
      const name = r?.data?.data?.company_name || r?.data?.company_name || '';
      setCompanyName(name);
    }).catch(() => {/* non-fatal — copy falls back to a.name */});
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, current_balance: formData.current_balance !== '' ? parseFloat(formData.current_balance) : null };
    try {
      if (isEditing) {
        await update(selectedId, payload);
        toast.success('Bank account updated');
      } else {
        await create(payload);
        toast.success('Bank account added');
      }
      setIsDialogOpen(false);
      setFormData(emptyForm);
      setIsEditing(false);
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Operation failed';
      toast.error(detail);
    }
  };

  const handleEdit = (a) => {
    setSelectedId(a.id);
    setFormData({ name: a.name || '', bank: a.bank || '', account_number: a.account_number || '', ifsc: a.ifsc || '', branch: a.branch || '', type: a.type || 'current', is_primary: a.is_primary || false, current_balance: String(a.current_balance ?? '') });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const handleShareWhatsApp = (a) => {
    const text = buildBankDetailsText(a, companyName);
    // Open WhatsApp's universal share URL — works on mobile (opens
    // WhatsApp app picker) and desktop (opens WhatsApp Web). Customer
    // picks the recipient on WhatsApp's own UI.
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopyDetails = async (a) => {
    const text = buildBankDetailsText(a, companyName);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-HTTPS / older browsers.
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Bank details copied — paste anywhere');
    } catch (e) {
      toast.error('Could not copy — please copy manually from the Edit dialog');
    }
  };

  const handleDelete = async (a) => {
    const confirmed = await confirm({ title: 'Delete Bank Account', description: `Are you sure you want to delete "${a.name}"? This action cannot be undone.` });
    if (!confirmed) return;
    try {
      await remove(a.id);
      toast.success('Account deleted');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  const handleSetPrimary = async (a) => {
    try {
      for (const acc of accounts) {
        if (acc.is_primary && acc.id !== a.id) {
          await update(acc.id, { ...acc, is_primary: false });
        }
      }
      await update(a.id, { ...a, is_primary: true });
      toast.success(`${a.name} set as primary`);
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const handleUpdateBalance = async () => {
    if (newBalance === '' || isNaN(parseFloat(newBalance))) { toast.error('Enter a valid amount'); return; }
    try {
      await update(balanceDialog.account.id, { ...balanceDialog.account, current_balance: parseFloat(newBalance) });
      toast.success('Balance updated');
      setBalanceDialog({ open: false, account: null });
      setNewBalance('');
      fetchData();
    } catch { toast.error('Failed to update balance'); }
  };

  const totalBalance = accounts.reduce((sum, a) => sum + (parseFloat(a.current_balance) || 0), 0);
  const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

  return (
    <div className="space-y-6" data-testid="bank-accounts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Bank Accounts</h1>
          <p className="text-muted-foreground">Manage bank accounts used for transactions</p>
        </div>
        <Button data-testid="add-bank-account-btn" onClick={() => { setFormData(emptyForm); setIsEditing(false); setIsDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />Add Account
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><Building2 className="h-8 w-8 text-primary" /><div><p className="text-2xl font-bold">{accounts.length}</p><p className="text-sm text-muted-foreground">Total Accounts</p></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><Wallet className="h-8 w-8 text-emerald-500" /><div><p className="text-2xl font-bold" data-testid="total-bank-balance">{fmt(totalBalance)}</p><p className="text-sm text-muted-foreground">Total Balance</p></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><Star className="h-8 w-8 text-amber-500" /><div><p className="text-2xl font-bold">{accounts.filter(a => a.is_primary).length}</p><p className="text-sm text-muted-foreground">Primary Accounts</p></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><Building2 className="h-8 w-8 text-blue-500" /><div><p className="text-2xl font-bold">{[...new Set(accounts.map(a => a.bank))].length}</p><p className="text-sm text-muted-foreground">Banks</p></div></div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>All Accounts</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-center py-8 text-muted-foreground">Loading...</p> :
          accounts.length === 0 ? <p className="text-center py-8 text-muted-foreground">No bank accounts added yet.</p> :
          <Table>
            <TableHeader><TableRow>
              <TableHead>Account Name</TableHead>
              <TableHead>Bank</TableHead>
              <TableHead>Account Number</TableHead>
              <TableHead>IFSC</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead>Primary</TableHead>
              <TableHead className="w-[60px]">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {accounts.map(a => (
                <TableRow key={a.id} data-testid={`bank-row-${a.id}`}>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell>{a.bank}</TableCell>
                  <TableCell className="font-mono text-sm">{a.account_number}</TableCell>
                  <TableCell className="font-mono text-sm">{a.ifsc}</TableCell>
                  <TableCell><Badge variant="outline" className="capitalize">{a.type}</Badge></TableCell>
                  <TableCell className="text-right font-semibold" data-testid={`bank-balance-${a.id}`}>
                    {a.current_balance != null ? fmt(a.current_balance) : <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell>
                    {a.is_primary ? <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30">Primary</Badge> :
                    <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => handleSetPrimary(a)}>Set Primary</Button>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-primary"
                        title="Copy bank details (WhatsApp/Email-ready)"
                        onClick={() => handleCopyDetails(a)}
                        data-testid={`bank-copy-${a.id}`}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-emerald-500"
                        title="Share via WhatsApp"
                        onClick={() => handleShareWhatsApp(a)}
                        data-testid={`bank-wa-${a.id}`}
                      >
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleCopyDetails(a)} data-testid={`bank-copy-menu-${a.id}`}>
                            <Copy className="h-3.5 w-3.5 mr-2" />Copy details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShareWhatsApp(a)} data-testid={`bank-wa-menu-${a.id}`}>
                            <MessageCircle className="h-3.5 w-3.5 mr-2" />Share via WhatsApp
                          </DropdownMenuItem>
                          {canEdit && (<DropdownMenuItem onClick={() => handleEdit(a)} data-testid={`bank-edit-${a.id}`}>Edit</DropdownMenuItem>)}
                          {canEdit && (<DropdownMenuItem onClick={() => { setBalanceDialog({ open: true, account: a }); setNewBalance(String(a.current_balance ?? '')); }}>Update Balance</DropdownMenuItem>)}
                          {canDelete && (<DropdownMenuItem className="text-red-600" onClick={() => handleDelete(a)} data-testid={`bank-delete-${a.id}`}>Delete</DropdownMenuItem>)}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent data-testid="bank-account-dialog">
          <DialogHeader><DialogTitle>{isEditing ? 'Edit' : 'Add'} Bank Account</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2"><Label>Account Display Name *</Label><Input data-testid="bank-name" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. HDFC Current A/c - 1234" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Bank Name *</Label><Input data-testid="bank-bank" required value={formData.bank} onChange={e => setFormData({ ...formData, bank: e.target.value })} placeholder="e.g. HDFC Bank" /></div>
              <div className="space-y-2"><Label>Account Type</Label>
                <Select value={formData.type} onValueChange={v => setFormData({ ...formData, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                  <SelectItem value="current">Current</SelectItem>
                  <SelectItem value="savings">Savings</SelectItem>
                  <SelectItem value="cc">Credit Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Account Number</Label><Input data-testid="bank-acno" value={formData.account_number} onChange={e => setFormData({ ...formData, account_number: e.target.value })} /></div>
              <div className="space-y-2"><Label>IFSC Code</Label><Input data-testid="bank-ifsc" value={formData.ifsc} onChange={e => setFormData({ ...formData, ifsc: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Branch <span className="text-xs text-muted-foreground">(optional — shown in copied details)</span></Label><Input data-testid="bank-branch" value={formData.branch} onChange={e => setFormData({ ...formData, branch: e.target.value })} placeholder="e.g. Andheri West, Mumbai" /></div>
            <div className="space-y-2"><Label>Current Balance</Label><Input data-testid="bank-balance" type="number" step="0.01" value={formData.current_balance} onChange={e => setFormData({ ...formData, current_balance: e.target.value })} placeholder="0.00" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="bank-submit-btn">{isEditing ? 'Update' : 'Add'} Account</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Update Balance Dialog */}
      <Dialog open={balanceDialog.open} onOpenChange={o => !o && setBalanceDialog({ open: false, account: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-emerald-500" />Update Balance</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Set the current balance for <span className="font-medium text-foreground">{balanceDialog.account?.name}</span></p>
            <div className="space-y-2">
              <Label>Current Balance (INR)</Label>
              <Input
                type="number"
                step="0.01"
                value={newBalance}
                onChange={e => setNewBalance(e.target.value)}
                placeholder="0.00"
                data-testid="update-balance-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBalanceDialog({ open: false, account: null })}>Cancel</Button>
            <Button onClick={handleUpdateBalance} data-testid="update-balance-btn">Save Balance</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
